import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { productsApi, type ProductListQuery } from "../api/products";
import type { ProductListItem } from "../api/types";
import { Shell } from "../app/Shell";
import {
  FAMILY_LABELS,
  PRODUCT_FAMILIES,
  isProductFamily,
} from "../catalog/taxonomy";
import type { ProductFamily } from "../catalog/taxonomy";
import { ErrorNotice } from "../components/ui";
import { DiscountsTable } from "./DiscountsTable";
import { RemoveDiscountDialog, SetDiscountDialog } from "./SetDiscountDialog";
import "./discounts.css";

/**
 * Discounts: the shop's sale prices in one place.
 *
 * IT OWNS NO PRICING. A discount in OptiQ is one column — `sale_price` on the
 * revision being worked on — and this screen reads it through the canonical
 * product listing and writes it through the canonical draft route. There is no
 * discount table, no promotion engine, no schedule and no coupon: none of those
 * exists in the domain, and inventing one here would be inventing a second
 * price model beside the one the storefront and the order path already read.
 *
 * WHAT "DISCOUNTED" MEANS HERE
 * A sale price that is SET and BELOW the base price — the same condition under
 * which an order records `original_unit_price_minor` and a saving. A sale price
 * equal to the base is storable and is not a discount, so it appears under "Not
 * discounted". The predicate is applied by the SERVER, as `sale_state`.
 *
 * EVERY NARROWING IS SERVER-SIDE. The result is one page, so a filter applied
 * in the browser would search the twenty-five rows in front of an administrator
 * and report that a discounted product on page two does not exist.
 */

/** "All" needs a spelling of its own.
 *
 *  An ABSENT parameter is the default — Discounted — so asking for every
 *  product cannot be expressed by removing it. `all` is that request written
 *  down, and it is the one value here that is not sent to the server: it means
 *  "no sale predicate", which is what omitting the field on the request does. */
const ALL = "all";

const SALE_STATES = [
  { value: "on_sale", label: "Discounted" },
  { value: "not_on_sale", label: "Not discounted" },
  { value: ALL, label: "All" },
] as const;

type SaleState = ProductListQuery["sale_state"];

/** What the page opens on.
 *
 *  Discounted, because the question this screen exists to answer is "what is
 *  the shop discounting right now". Setting a NEW discount means switching to
 *  All or Not discounted, which is the less frequent errand. */
const DEFAULT_SALE_STATE = "on_sale";

/** How many products one page of the discounts table shows. */
const DISCOUNTS_PAGE_SIZE = 25;

/** The URL's answer, resolved to what the request should carry.
 *
 *  Absent is the default; `all` is no predicate; anything unrecognised falls
 *  back to the default rather than quietly widening the view a bookmark asked
 *  to narrow. */
function readSaleState(raw: string | null): SaleState {
  if (raw === null) return DEFAULT_SALE_STATE;
  if (raw === "on_sale" || raw === "not_on_sale") return raw;
  if (raw === ALL) return undefined;
  return DEFAULT_SALE_STATE;
}

export function DiscountsPage() {
  // Query state lives in the URL so a filtered view can be shared or reloaded,
  // following Products and Inventory. Nothing here is personal data: a product
  // name and a sale state are facts about the shop.
  const [params, setParams] = useSearchParams();
  const [editing, setEditing] = useState<ProductListItem | null>(null);
  const [removing, setRemoving] = useState<ProductListItem | null>(null);

  const search = params.get("search") ?? "";
  const familyParam = params.get("family");
  const family: ProductFamily | null = isProductFamily(familyParam)
    ? familyParam
    : null;
  // Absent means the default rather than "all": a screen called Discounts that
  // opened on the whole catalogue would bury the thing it is for.
  const saleParam = params.get("sale_state");
  const saleState: SaleState = readSaleState(saleParam);
  const page = Number(params.get("page") ?? "1") || 1;

  // ONE request per rendered page, whatever the family and whatever the page.
  //
  // The server resolves a family to its stored categories — Glasses is
  // `sunglasses` AND `eyeglasses` — so there is nothing to compose here. The
  // alternative, asking per category and merging, needs pages 1..N of EVERY
  // category to render page N: page 20 of Glasses would be forty requests, and
  // the cost would grow with the page number. Pagination is the server's.
  const listQuery: ProductListQuery = {
    search: search || undefined,
    family: family ?? undefined,
    // THE WORKING REVISION, not the live one. A discount staged in an open
    // draft is a discount somebody is managing; filtering it out because
    // customers cannot see it yet would hide the very edit this screen exists
    // for. The server applies the same revision to the prices it returns and
    // to the sale filter, so a row cannot be filtered on one and rendered from
    // another. What customers see is unchanged and stays unchanged until the
    // normal Publish Changes flow.
    revision_scope: "working",
    sale_state: saleState,
    page,
    page_size: DISCOUNTS_PAGE_SIZE,
  };
  const query = useQuery({
    queryKey: ["products", { ...listQuery }],
    queryFn: ({ signal }) => productsApi.list(listQuery, signal),
    // The previous page stays on screen while the next one loads, so paging
    // does not blank the table between answers.
    placeholderData: (previous) => previous,
  });
  const data = query.data ?? null;

  /** Any change to what is being asked restarts paging: page three of the old
   *  result set is meaningless against a new one. */
  function update(next: Record<string, string>) {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value) merged.set(key, value);
      else merged.delete(key);
    }
    if (!("page" in next)) merged.delete("page");
    setParams(merged, { replace: true });
  }

  /** The dialogs belong to a row that the next page may not contain. */
  function closeDialogs() {
    setEditing(null);
    setRemoving(null);
  }

  const narrowed = Boolean(search || family) || saleState !== DEFAULT_SALE_STATE;

  return (
    <Shell title="Discounts">
      <div className="stack">
        <div className="discounts__toolbar">
          <div className="discounts__search">
            <label className="sr-only" htmlFor="discount-search">
              Search products
            </label>
            <input
              id="discount-search"
              type="search"
              placeholder="Search by product name or product code"
              defaultValue={search}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  closeDialogs();
                  update({ search: event.currentTarget.value.trim() });
                }
              }}
              onBlur={(event) => {
                if (event.currentTarget.value.trim() !== search) {
                  closeDialogs();
                  update({ search: event.currentTarget.value.trim() });
                }
              }}
            />
          </div>

          <div className="discounts__filter">
            <label className="sr-only" htmlFor="discount-family">
              Product family
            </label>
            <select
              id="discount-family"
              value={family ?? ""}
              onChange={(event) => {
                closeDialogs();
                update({ family: event.target.value });
              }}
            >
              <option value="">All families</option>
              {PRODUCT_FAMILIES.map((key) => (
                <option key={key} value={key}>
                  {FAMILY_LABELS[key]}
                </option>
              ))}
            </select>
          </div>

          <div className="discounts__filter">
            <label className="sr-only" htmlFor="discount-sale-state">
              Discount state
            </label>
            <select
              id="discount-sale-state"
              value={saleState ?? ALL}
              onChange={(event) => {
                closeDialogs();
                // The default is what an absent parameter means, so choosing it
                // clears the parameter rather than writing it back. Every other
                // choice — including `all` — is written down, because there is
                // no other way to say it.
                update({
                  sale_state:
                    event.target.value === DEFAULT_SALE_STATE
                      ? ""
                      : event.target.value,
                });
              }}
            >
              {SALE_STATES.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="spacer" />

          {data ? (
            <span className="muted num">
              {data.total} product{data.total === 1 ? "" : "s"}
            </span>
          ) : null}
        </div>

        {/* Retry sits beside the message rather than inside it, so the alert an
            assistive reader announces is the sentence and not the button. */}
        {query.error ? (
          <div className="discounts__failure">
            <ErrorNotice error={query.error} />
            <button
              type="button"
              className="btn"
              disabled={query.isFetching}
              onClick={() => query.refetch()}
            >
              {query.isFetching ? "Retrying…" : "Retry"}
            </button>
          </div>
        ) : null}

        <div className="panel">
          {query.isPending ? (
            <div className="empty" role="status">
              Loading products…
            </div>
          ) : query.error ? null : data && data.items.length === 0 ? (
            <div className="empty">
              {saleState === "on_sale" && !narrowed
                ? "No products are discounted. Switch to All to set one."
                : "No products match these filters."}
            </div>
          ) : data ? (
            <DiscountsTable
              items={data.items}
              onSetDiscount={setEditing}
              onRemoveDiscount={setRemoving}
            />
          ) : null}
        </div>

        {data && data.total_pages > 1 ? (
          <div className="row" aria-label="Pagination">
            <button
              type="button"
              className="btn btn--sm"
              disabled={page <= 1}
              onClick={() => {
                closeDialogs();
                update({ page: String(page - 1) });
              }}
            >
              Previous
            </button>
            <span className="muted num" role="status">
              Page {data.page} of {data.total_pages}
            </span>
            <button
              type="button"
              className="btn btn--sm"
              disabled={page >= data.total_pages}
              onClick={() => {
                closeDialogs();
                update({ page: String(page + 1) });
              }}
            >
              Next
            </button>
          </div>
        ) : null}
      </div>

      {/* Keyed on the prices the dialog was opened against, so a refetch that
          moves the product underneath it remounts the form rather than leaving
          a stale base price beside a live preview. */}
      {editing ? (
        <SetDiscountDialog
          key={`${editing.product_id}-${editing.base_price}-${editing.sale_price}`}
          item={
            data?.items.find((row) => row.product_id === editing.product_id) ??
            editing
          }
          onClose={() => setEditing(null)}
        />
      ) : null}

      {removing ? (
        <RemoveDiscountDialog
          key={`${removing.product_id}-${removing.sale_price}`}
          item={
            data?.items.find((row) => row.product_id === removing.product_id) ??
            removing
          }
          onClose={() => setRemoving(null)}
        />
      ) : null}
    </Shell>
  );
}
